import React, { useRef, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AppChip from "../../../common/app-chip";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";
import typography from "../../../cssUtils/typography";
import { MAX_LABELS, MAX_LABEL_LENGTH } from "../utils";

// Inline label editor for a single Nodebot/CustomChatbot/FallbackAutoresponder row —
// rendered directly in the Workflows list tables (not just the edit drawers). Adding
// or removing a chip calls `onChange` with the full next label list; the caller owns
// persisting it (see WorkFlow.js's handleLabelsChange) and passing back the saved
// value as `labels`. A draft label is never saved implicitly — the user must hit the
// save action (or Enter); clicking away with unsaved text prompts a save/discard choice
// instead of committing or dropping it silently.
const LabelsCell = ({ labels, onChange, saving }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const editorRef = useRef(null);

  const cancelAdd = () => {
    setAdding(false);
    setDraft("");
    setError("");
    setConfirmOpen(false);
  };

  const startAdd = () => {
    setAdding(true);
    setDraft("");
    setError("");
  };

  // Validates and persists the draft. Returns false (and leaves the editor open)
  // only when the draft fails validation, so callers can decide what to do next.
  const commitAdd = () => {
    const value = draft.trim();
    if (!value) {
      cancelAdd();
      return true;
    }
    if (value.length > MAX_LABEL_LENGTH) {
      setError(`Max ${MAX_LABEL_LENGTH} characters`);
      return false;
    }
    if (labels.some((label) => label.toLowerCase() === value.toLowerCase())) {
      cancelAdd();
      return true;
    }
    if (labels.length >= MAX_LABELS) {
      setError(`Up to ${MAX_LABELS} labels`);
      return false;
    }
    cancelAdd();
    onChange([...labels, value]);
    return true;
  };

  const removeLabel = (index) => {
    onChange(labels.filter((_, i) => i !== index));
  };

  // Focus leaving the editor (click elsewhere, tab away) never auto-saves. If there's
  // unsaved text, surface the confirm dialog instead of silently keeping or dropping it.
  const handleEditorBlur = (e) => {
    if (editorRef.current && editorRef.current.contains(e.relatedTarget)) return;
    if (draft.trim()) {
      setConfirmOpen(true);
    } else {
      cancelAdd();
    }
  };

  const handleConfirmSave = () => {
    setConfirmOpen(false);
    commitAdd();
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, maxWidth: 260 }}
    >
      {labels.map((label, index) => (
        <AppChip
          key={`${label}-${index}`}
          label={label}
          onDelete={saving ? undefined : () => removeLabel(index)}
          sx={{ backgroundColor: "#F3F4F6", color: "#374151" }}
        />
      ))}

      {saving ? (
        <CircularProgress size={14} sx={{ color: "#9CA3AF" }} />
      ) : adding ? (
        <Box ref={editorRef} onBlur={handleEditorBlur} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <TextField
            autoFocus
            size="small"
            variant="standard"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitAdd();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelAdd();
              }
            }}
            error={!!error}
            placeholder="Label"
            inputProps={{ maxLength: MAX_LABEL_LENGTH }}
            sx={{ width: 90, "& .MuiInputBase-input": { ...typography.bodySmall, py: 0.25 } }}
          />
          <Tooltip title="Save label" placement="top" arrow>
            <IconButton
              size="small"
              onClick={commitAdd}
              sx={{ width: 20, height: 20, color: "#2E7D32", "&:hover": { bgcolor: "#E6F4EA" } }}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" placement="top" arrow>
            <IconButton
              size="small"
              onClick={cancelAdd}
              sx={{ width: 20, height: 20, color: "#E05252", "&:hover": { bgcolor: "#FDECEC" } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        labels.length < MAX_LABELS && (
          <Tooltip title="Add label" placement="top" arrow>
            <IconButton
              size="small"
              onClick={startAdd}
              sx={{ width: 22, height: 22, color: "#6C7C93", "&:hover": { bgcolor: "#F0F2F8", color: "#1D2330" } }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )
      )}

      {error && (
        <Typography sx={{ ...typography.bodySmall, color: "#E05252", flexBasis: "100%" }}>
          {error}
        </Typography>
      )}

      <Dialog
        open={confirmOpen}
        onClose={(e, reason) => {
          e?.stopPropagation?.();
          if (reason === "backdropClick") return; // force an explicit save/discard choice
          setConfirmOpen(false);
        }}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ ...typography.h1 }}>Unsaved label</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typography.bodyLarge, color: "#505050" }}>
            You have an unsaved label "{draft.trim()}". Save it or discard the change.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <ButtonTwo onClick={cancelAdd} variant="outlined">
            Discard
          </ButtonTwo>
          <ButtonOne onClick={handleConfirmSave} variant="contained">
            Save
          </ButtonOne>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelsCell;
